import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  IconButton,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NexusFile {
  id: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  createdAt: string;
}

interface NexusComplianceItem {
  requirementApiName: string | null;
  requirementName: string | null;
  sectionApiName: string | null;
  sectionName: string | null;
  status: string | null;
  isCompleted: boolean;
  fileId: string | null;
  requiresDocument: boolean;
  allowManualCompletion: boolean;
}

interface DocumentConfig {
  name: string;
  twoSided?: boolean;
  expirationField?: string;
}

interface TabConfig {
  label: string;
  documents: DocumentConfig[];
}

interface FormConfig {
  tabs: TabConfig[];
}

interface FileUploadProps {
  formData: Record<string, any>;
  formName: string;
  layout?: 'tabs' | 'stacked';
  themeName?: 'default' | 'validation';
}

// ---------------------------------------------------------------------------
// Color themes (matching SB)
// ---------------------------------------------------------------------------

const themes = {
  default: {
    deep: '#330662',
    ultra: '#8900e1',
    medium: '#702b9d',
    uploaded: '#8900e1',
    notUploaded: '#702b9d',
  },
  validation: {
    deep: '#d32f2f',
    ultra: '#4caf50',
    medium: '#f44336',
    uploaded: '#4caf50',
    notUploaded: '#d32f2f',
  },
};

// ---------------------------------------------------------------------------
// File-name normalization (mirrors Nexus complianceService logic)
// ---------------------------------------------------------------------------

const KNOWN_UPLOAD_EXTENSION_RE = /\.(pdf|png|jpe?g|docx?)$/i;

function normalizeFileStem(name: string): string {
  return name
    .replace(KNOWN_UPLOAD_EXTENSION_RE, '') // strip known upload extensions only
    .replace(/[\\/?:%*"<>|]/g, ' ') // normalize filename separators before stripping punctuation
    .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase → spaces
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // remove special chars
    .replace(/\s+/g, ' ')
    .trim();
}

function isDocumentUploaded(documentName: string, caregiverFiles: NexusFile[]): boolean {
  const normalized = normalizeFileStem(documentName);
  return caregiverFiles.some((f) => {
    const fileNorm = normalizeFileStem(f.fileName);
    return fileNorm === normalized || fileNorm.endsWith(` ${normalized}`);
  });
}

const COMPLIANCE_DONE_STATUSES = new Set(['COMPLIANT', 'EXPIRING']);

const COMPLIANCE_DOCUMENT_ALIASES: Record<string, string[]> = {
  'Assistance with Medication Admin': ['Assistance with Med Admin', 'assistance_med_admin'],
  'Elder Abuse and Neglect': ['Elder Abuse & Neglect', 'elder_abuse_neglect'],
};

const COMPLIANCE_SECTION_BY_TAB: Record<string, string> = {
  PersonalDocuments: 'personal_documents',
  InServices: 'in_service_training',
};

function normalizeComplianceKey(value: string): string {
  return normalizeFileStem(value.replace(/_/g, ' '));
}

function getMatchingComplianceItems(
  documentName: string,
  complianceItems: NexusComplianceItem[],
  sectionApiName?: string
): NexusComplianceItem[] {
  const documentKeys = new Set([
    documentName,
    ...(COMPLIANCE_DOCUMENT_ALIASES[documentName] || []),
  ].map(normalizeComplianceKey));

  return complianceItems.filter((item) => {
    if (sectionApiName && item.sectionApiName !== sectionApiName) {
      return false;
    }

    const requirementKeys = [item.requirementName, item.requirementApiName]
      .filter((value): value is string => typeof value === 'string' && value.length > 0)
      .map(normalizeComplianceKey);

    return requirementKeys.some((key) => documentKeys.has(key));
  });
}

function isDocumentRelevantToCompliance(
  documentName: string,
  complianceItems: NexusComplianceItem[],
  complianceAvailable: boolean,
  sectionApiName?: string
): boolean {
  // Preserve the existing upload list only when Nexus compliance could not be
  // loaded. A successfully loaded empty view is authoritative.
  if (!complianceAvailable) return true;

  return getMatchingComplianceItems(documentName, complianceItems, sectionApiName)
    .some((item) => item.requiresDocument && item.status?.trim().toUpperCase() !== 'NA');
}

function isDocumentSatisfiedByCompliance(
  documentName: string,
  complianceItems: NexusComplianceItem[]
): boolean {
  return getMatchingComplianceItems(documentName, complianceItems).some((item) => {
    const normalizedStatus = item.status?.trim().toUpperCase();
    if (!normalizedStatus || !COMPLIANCE_DONE_STATUSES.has(normalizedStatus)) {
      return false;
    }

    return true;
  });
}

// ---------------------------------------------------------------------------
// Document config per caregiver type (Nexus field names)
// ---------------------------------------------------------------------------

const formConfig: Record<string, FormConfig> = {
  HHA: {
    tabs: [
      {
        label: 'PersonalDocuments',
        documents: [
          { name: 'Drivers License', twoSided: true, expirationField: 'driversLicenseExpiration' },
          { name: 'Florida State ID', twoSided: true, expirationField: 'floridaStateIdExpiration' },
          { name: 'Social Security Card', twoSided: true },
          { name: 'Employment Auth', twoSided: true, expirationField: 'sysEmploymentAuthExpiration' },
          { name: 'HHA Certificate' },
          { name: 'CPR Certificate', expirationField: 'cprCertificateExpiration' },
          { name: 'Physical', expirationField: 'physicalExpiration' },
          { name: 'Liability Insurance', expirationField: 'liabilityInsuranceExpiration' },
          { name: 'Direct Deposit Form' },
          { name: 'Car Insurance', expirationField: 'carInsuranceExpiration' },
        ],
      },
      {
        label: 'InServices',
        documents: [
          { name: 'Elder Affairs Certificate' },
          { name: 'Alzheimers' },
          { name: 'Elder Abuse and Neglect' },
          { name: 'HIPAA' },
          { name: 'HIV' },
          { name: 'Assistance with Medication Admin' },
          { name: 'Domestic Violence' },
          { name: 'OSHA' },
          { name: 'Infection Control' },
          { name: 'Blood Borne Pathogens' },
          { name: 'Universal Precautions' },
        ],
      },
    ],
  },
  CNA: {
    tabs: [
      {
        label: 'PersonalDocuments',
        documents: [
          { name: 'Drivers License', twoSided: true, expirationField: 'driversLicenseExpiration' },
          { name: 'Florida State ID', twoSided: true, expirationField: 'floridaStateIdExpiration' },
          { name: 'Social Security Card', twoSided: true },
          { name: 'Employment Auth', twoSided: true, expirationField: 'sysEmploymentAuthExpiration' },
          { name: 'CNA License', expirationField: 'cnaLicenseExpiration' },
          { name: 'CPR Certificate', expirationField: 'cprCertificateExpiration' },
          { name: 'Physical', expirationField: 'physicalExpiration' },
          { name: 'Liability Insurance', expirationField: 'liabilityInsuranceExpiration' },
          { name: 'Direct Deposit Form' },
          { name: 'Car Insurance', expirationField: 'carInsuranceExpiration' },
        ],
      },
      {
        label: 'InServices',
        documents: [
          { name: 'Elder Affairs Certificate' },
          { name: 'Alzheimers' },
          { name: 'Elder Abuse and Neglect' },
          { name: 'HIPAA' },
          { name: 'HIV' },
          { name: 'Assistance with Medication Admin' },
          { name: 'Domestic Violence' },
          { name: 'Medical Errors' },
          { name: 'OSHA' },
          { name: 'Infection Control' },
          { name: 'Blood Borne Pathogens' },
          { name: 'Universal Precautions' },
        ],
      },
    ],
  },
  RN: {
    tabs: [
      {
        label: 'PersonalDocuments',
        documents: [
          { name: 'Drivers License', twoSided: true, expirationField: 'driversLicenseExpiration' },
          { name: 'Florida State ID', twoSided: true, expirationField: 'floridaStateIdExpiration' },
          { name: 'Social Security Card', twoSided: true },
          { name: 'Employment Auth', twoSided: true, expirationField: 'sysEmploymentAuthExpiration' },
          { name: 'RN License', expirationField: 'rnLicenseExpiration' },
          { name: 'CPR Certificate', expirationField: 'cprCertificateExpiration' },
          { name: 'Physical', expirationField: 'physicalExpiration' },
          { name: 'Liability Insurance', expirationField: 'liabilityInsuranceExpiration' },
          { name: 'Direct Deposit Form' },
          { name: 'Car Insurance', expirationField: 'carInsuranceExpiration' },
        ],
      },
      {
        label: 'InServices',
        documents: [
          { name: 'Elder Affairs Certificate' },
          { name: 'Alzheimers' },
          { name: 'Elder Abuse and Neglect' },
          { name: 'HIPAA' },
          { name: 'HIV' },
          { name: 'Domestic Violence' },
          { name: 'Medical Errors' },
          { name: 'OSHA' },
          { name: 'Infection Control' },
          { name: 'Blood Borne Pathogens' },
          { name: 'Universal Precautions' },
        ],
      },
    ],
  },
  LPN: {
    tabs: [
      {
        label: 'PersonalDocuments',
        documents: [
          { name: 'Drivers License', twoSided: true, expirationField: 'driversLicenseExpiration' },
          { name: 'Florida State ID', twoSided: true, expirationField: 'floridaStateIdExpiration' },
          { name: 'Social Security Card', twoSided: true },
          { name: 'Employment Auth', twoSided: true, expirationField: 'sysEmploymentAuthExpiration' },
          { name: 'LPN License', expirationField: 'lpnLicenseExpiration' },
          { name: 'CPR Certificate', expirationField: 'cprCertificateExpiration' },
          { name: 'Physical', expirationField: 'physicalExpiration' },
          { name: 'Liability Insurance', expirationField: 'liabilityInsuranceExpiration' },
          { name: 'Direct Deposit Form' },
          { name: 'Car Insurance', expirationField: 'carInsuranceExpiration' },
        ],
      },
      {
        label: 'InServices',
        documents: [
          { name: 'Elder Affairs Certificate' },
          { name: 'Alzheimers' },
          { name: 'Elder Abuse and Neglect' },
          { name: 'HIPAA' },
          { name: 'HIV' },
          { name: 'Domestic Violence' },
          { name: 'Medical Errors' },
          { name: 'OSHA' },
          { name: 'Infection Control' },
          { name: 'Blood Borne Pathogens' },
          { name: 'Universal Precautions' },
        ],
      },
    ],
  },
  OT: {
    tabs: [
      {
        label: 'PersonalDocuments',
        documents: [
          { name: 'Drivers License', twoSided: true, expirationField: 'driversLicenseExpiration' },
          { name: 'Florida State ID', twoSided: true, expirationField: 'floridaStateIdExpiration' },
          { name: 'Social Security Card', twoSided: true },
          { name: 'Employment Auth', twoSided: true, expirationField: 'sysEmploymentAuthExpiration' },
          { name: 'OT/OTA License', expirationField: 'otOtaLicenseExpiration' },
          { name: 'CPR Certificate', expirationField: 'cprCertificateExpiration' },
          { name: 'Physical', expirationField: 'physicalExpiration' },
          { name: 'Liability Insurance', expirationField: 'liabilityInsuranceExpiration' },
          { name: 'Direct Deposit Form' },
          { name: 'Car Insurance', expirationField: 'carInsuranceExpiration' },
        ],
      },
      {
        label: 'InServices',
        documents: [
          { name: 'Elder Affairs Certificate' },
          { name: 'Alzheimers' },
          { name: 'Elder Abuse and Neglect' },
          { name: 'HIPAA' },
          { name: 'HIV' },
          { name: 'Domestic Violence' },
          { name: 'Medical Errors' },
          { name: 'OSHA' },
          { name: 'Infection Control' },
          { name: 'Blood Borne Pathogens' },
          { name: 'Universal Precautions' },
          { name: 'Fl. Laws/Rules of OT' },
        ],
      },
    ],
  },
  OTA: {
    tabs: [
      {
        label: 'PersonalDocuments',
        documents: [
          { name: 'Drivers License', twoSided: true, expirationField: 'driversLicenseExpiration' },
          { name: 'Florida State ID', twoSided: true, expirationField: 'floridaStateIdExpiration' },
          { name: 'Social Security Card', twoSided: true },
          { name: 'Employment Auth', twoSided: true, expirationField: 'sysEmploymentAuthExpiration' },
          { name: 'OT/OTA License', expirationField: 'otOtaLicenseExpiration' },
          { name: 'CPR Certificate', expirationField: 'cprCertificateExpiration' },
          { name: 'Physical', expirationField: 'physicalExpiration' },
          { name: 'Liability Insurance', expirationField: 'liabilityInsuranceExpiration' },
          { name: 'Direct Deposit Form' },
          { name: 'Car Insurance', expirationField: 'carInsuranceExpiration' },
        ],
      },
      {
        label: 'InServices',
        documents: [
          { name: 'Elder Affairs Certificate' },
          { name: 'Alzheimers' },
          { name: 'Elder Abuse and Neglect' },
          { name: 'HIPAA' },
          { name: 'HIV' },
          { name: 'Domestic Violence' },
          { name: 'Medical Errors' },
          { name: 'OSHA' },
          { name: 'Infection Control' },
          { name: 'Blood Borne Pathogens' },
          { name: 'Universal Precautions' },
          { name: 'Fl. Laws/Rules of OT' },
        ],
      },
    ],
  },
  PT: {
    tabs: [
      {
        label: 'PersonalDocuments',
        documents: [
          { name: 'Drivers License', twoSided: true, expirationField: 'driversLicenseExpiration' },
          { name: 'Florida State ID', twoSided: true, expirationField: 'floridaStateIdExpiration' },
          { name: 'Social Security Card', twoSided: true },
          { name: 'Employment Auth', twoSided: true, expirationField: 'sysEmploymentAuthExpiration' },
          { name: 'PT/PTA License', expirationField: 'ptPtaLicenseExpiration' },
          { name: 'CPR Certificate', expirationField: 'cprCertificateExpiration' },
          { name: 'Physical', expirationField: 'physicalExpiration' },
          { name: 'Liability Insurance', expirationField: 'liabilityInsuranceExpiration' },
          { name: 'Direct Deposit Form' },
          { name: 'Car Insurance', expirationField: 'carInsuranceExpiration' },
        ],
      },
      {
        label: 'InServices',
        documents: [
          { name: 'Elder Affairs Certificate' },
          { name: 'Alzheimers' },
          { name: 'Elder Abuse and Neglect' },
          { name: 'HIPAA' },
          { name: 'HIV' },
          { name: 'Domestic Violence' },
          { name: 'Medical Errors' },
          { name: 'OSHA' },
          { name: 'Infection Control' },
          { name: 'Blood Borne Pathogens' },
          { name: 'Universal Precautions' },
        ],
      },
    ],
  },
  PTA: {
    tabs: [
      {
        label: 'PersonalDocuments',
        documents: [
          { name: 'Drivers License', twoSided: true, expirationField: 'driversLicenseExpiration' },
          { name: 'Florida State ID', twoSided: true, expirationField: 'floridaStateIdExpiration' },
          { name: 'Social Security Card', twoSided: true },
          { name: 'Employment Auth', twoSided: true, expirationField: 'sysEmploymentAuthExpiration' },
          { name: 'PT/PTA License', expirationField: 'ptPtaLicenseExpiration' },
          { name: 'CPR Certificate', expirationField: 'cprCertificateExpiration' },
          { name: 'Physical', expirationField: 'physicalExpiration' },
          { name: 'Liability Insurance', expirationField: 'liabilityInsuranceExpiration' },
          { name: 'Direct Deposit Form' },
          { name: 'Car Insurance', expirationField: 'carInsuranceExpiration' },
        ],
      },
      {
        label: 'InServices',
        documents: [
          { name: 'Elder Affairs Certificate' },
          { name: 'Alzheimers' },
          { name: 'Elder Abuse and Neglect' },
          { name: 'HIPAA' },
          { name: 'HIV' },
          { name: 'Domestic Violence' },
          { name: 'Medical Errors' },
          { name: 'OSHA' },
          { name: 'Infection Control' },
          { name: 'Blood Borne Pathogens' },
          { name: 'Universal Precautions' },
        ],
      },
    ],
  },
  SLP: {
    tabs: [
      {
        label: 'PersonalDocuments',
        documents: [
          { name: 'Drivers License', twoSided: true, expirationField: 'driversLicenseExpiration' },
          { name: 'Florida State ID', twoSided: true, expirationField: 'floridaStateIdExpiration' },
          { name: 'Social Security Card', twoSided: true },
          { name: 'Employment Auth', twoSided: true, expirationField: 'sysEmploymentAuthExpiration' },
          { name: 'CPR Certificate', expirationField: 'cprCertificateExpiration' },
          { name: 'Physical', expirationField: 'physicalExpiration' },
          { name: 'Liability Insurance', expirationField: 'liabilityInsuranceExpiration' },
          { name: 'Direct Deposit Form' },
          { name: 'Car Insurance', expirationField: 'carInsuranceExpiration' },
        ],
      },
      {
        label: 'InServices',
        documents: [
          { name: 'Elder Affairs Certificate' },
          { name: 'Alzheimers' },
          { name: 'Elder Abuse and Neglect' },
          { name: 'HIPAA' },
          { name: 'HIV' },
          { name: 'Domestic Violence' },
          { name: 'Medical Errors' },
          { name: 'OSHA' },
          { name: 'Infection Control' },
          { name: 'Blood Borne Pathogens' },
          { name: 'Universal Precautions' },
        ],
      },
    ],
  },
};

// ---------------------------------------------------------------------------
// Theme type
// ---------------------------------------------------------------------------

type Theme = typeof themes.default;

// ---------------------------------------------------------------------------
// TwoSidedUploadZone
// ---------------------------------------------------------------------------

interface TwoSidedUploadZoneProps {
  itemTitle: string;
  documentName: string;
  isUploaded: boolean;
  isExpired: boolean;
  theme: Theme;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
  onUploadSuccess: () => void;
}

const TwoSidedUploadZone: React.FC<TwoSidedUploadZoneProps> = ({
  itemTitle,
  documentName,
  isUploaded,
  isExpired,
  theme,
  authFetch,
  onUploadSuccess,
}) => {
  const { t } = useTranslation();
  const [frontFile, setFrontFile] = useState<File | null>(null);
  const [backFile, setBackFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadState, setUploadState] = useState<'idle' | 'uploading' | 'success' | 'error'>(
    isUploaded && !isExpired ? 'success' : 'idle'
  );
  const [errorMessage, setErrorMessage] = useState('');
  const frontInputRef = useRef<HTMLInputElement>(null);
  const backInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File) => {
    // Two-sided uploads are front/back scans, so keep this limited to PDF/image formats.
    const allowed = ['.pdf', '.png', '.jpg', '.jpeg'];
    const name = file.name.toLowerCase();
    const ext = name.substring(name.lastIndexOf('.'));
    return allowed.includes(ext);
  };

  const handleFileSelection = (file: File, side: 'front' | 'back') => {
    if (!validateFile(file)) {
      setUploadState('error');
      setErrorMessage(t('InvalidFileType', 'Invalid file type'));
      setTimeout(() => { setUploadState('idle'); setErrorMessage(''); }, 5000);
      return;
    }
    if (side === 'front') setFrontFile(file);
    else setBackFile(file);
  };

  const handleCombinedUpload = useCallback(async () => {
    if (!frontFile || !backFile) return;
    setUploadState('uploading');
    setUploadProgress(10);

    try {
      const formData = new FormData();
      formData.append('frontFile', frontFile);
      formData.append('backFile', backFile);
      formData.append('documentName', documentName);
      formData.append('isTwoSided', 'true');

      const response = await authFetch('/api/caregiver/files', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Upload failed');
      }

      setUploadProgress(100);
      setUploadState('success');
      onUploadSuccess();
    } catch (error: any) {
      console.error('Upload failed:', error);
      setUploadState('error');
      setErrorMessage(error.message || t('UploadFailed', 'Upload failed'));
      setFrontFile(null);
      setBackFile(null);
      setTimeout(() => { setUploadState('idle'); setErrorMessage(''); }, 5000);
    }
  }, [frontFile, backFile, documentName, authFetch, onUploadSuccess, t]);

  useEffect(() => {
    if (frontFile && backFile && uploadState === 'idle') {
      handleCombinedUpload();
    }
  }, [frontFile, backFile, uploadState, handleCombinedUpload]);

  const getFrontStatus = () => (uploadState === 'success' || frontFile) ? 'uploaded' : 'idle';
  const getBackStatus = () => (uploadState === 'success' || backFile) ? 'uploaded' : 'idle';

  return (
    <Card
      sx={{
        width: { xs: '100%', sm: 215 },
        margin: { xs: 0, sm: 1.5 },
        height: { xs: 'auto', sm: '100%' },
        display: 'flex',
        flexDirection: 'column',
        borderColor: theme.deep,
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': { boxShadow: '0 4px 8px rgba(51, 6, 98, 0.2)' },
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto',
          minHeight: { xs: 30, sm: 40 },
          height: { xs: 'auto', sm: 40 },
          padding: { xs: 0.5, sm: 1 },
          backgroundColor: uploadState === 'success' ? theme.uploaded : theme.notUploaded,
          color: 'white',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          textAlign: 'center',
          transition: 'background-color 0.3s ease',
        }}
      >
        <Typography
          variant="subtitle1"
          component="div"
          sx={{
            width: '100%',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            hyphens: 'auto',
            lineHeight: 1,
            fontSize: { xs: '0.65rem', sm: '0.875rem' },
          }}
        >
          {itemTitle}
        </Typography>
      </CardContent>

      <CardContent
        sx={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          padding: { xs: 0.75, sm: 1.5 },
          gap: 1,
          minHeight: { xs: 180, sm: 220 },
        }}
      >
        {/* Front side */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: uploadState === 'success' ? 'default' : 'pointer',
            border: `2px dashed ${getFrontStatus() === 'uploaded' ? theme.uploaded : theme.notUploaded}`,
            borderRadius: 1,
            padding: 1,
            backgroundColor: getFrontStatus() === 'uploaded' ? `${theme.uploaded}10` : 'transparent',
            transition: 'all 0.3s ease',
            '&:hover': uploadState !== 'success' ? { backgroundColor: `${theme.medium}10`, borderColor: theme.medium } : {},
          }}
          onClick={() => uploadState !== 'success' && frontInputRef.current?.click()}
        >
          <input
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            ref={frontInputRef}
            style={{ display: 'none' }}
            onChange={(e) => e.target.files?.length && handleFileSelection(e.target.files[0], 'front')}
          />
          <Typography variant="caption" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' }, mb: 0.5 }}>
            {t('Front', 'Front')}
          </Typography>
          {getFrontStatus() === 'uploaded' ? (
            <CheckCircleIcon sx={{ fontSize: { xs: 16, sm: 20 }, color: theme.uploaded }} />
          ) : (
            <CloudUploadIcon sx={{ fontSize: { xs: 16, sm: 20 }, color: theme.notUploaded }} />
          )}
        </Box>

        {/* Back side */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: uploadState === 'success' ? 'default' : 'pointer',
            border: `2px dashed ${getBackStatus() === 'uploaded' ? theme.uploaded : theme.notUploaded}`,
            borderRadius: 1,
            padding: 1,
            backgroundColor: getBackStatus() === 'uploaded' ? `${theme.uploaded}10` : 'transparent',
            transition: 'all 0.3s ease',
            '&:hover': uploadState !== 'success' ? { backgroundColor: `${theme.medium}10`, borderColor: theme.medium } : {},
          }}
          onClick={() => uploadState !== 'success' && backInputRef.current?.click()}
        >
          <input
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            ref={backInputRef}
            style={{ display: 'none' }}
            onChange={(e) => e.target.files?.length && handleFileSelection(e.target.files[0], 'back')}
          />
          <Typography variant="caption" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' }, mb: 0.5 }}>
            {t('Back', 'Back')}
          </Typography>
          {getBackStatus() === 'uploaded' ? (
            <CheckCircleIcon sx={{ fontSize: { xs: 16, sm: 20 }, color: theme.uploaded }} />
          ) : (
            <CloudUploadIcon sx={{ fontSize: { xs: 16, sm: 20 }, color: theme.notUploaded }} />
          )}
        </Box>

        {/* Status messages */}
        {uploadState === 'uploading' && (
          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <CircularProgress size={16} sx={{ color: theme.medium }} />
            <Typography variant="caption" sx={{ fontSize: { xs: '0.55rem', sm: '0.65rem' }, display: 'block' }}>
              {t('Uploading', { percentage: uploadProgress, defaultValue: 'Uploading... {{percentage}}%' })}
            </Typography>
          </Box>
        )}
        {uploadState === 'error' && (
          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
            <Typography variant="caption" sx={{ fontSize: { xs: '0.55rem', sm: '0.65rem' }, color: 'error.main', display: 'block' }}>
              {errorMessage}
            </Typography>
          </Box>
        )}
        {uploadState === 'success' && (
          <Typography variant="caption" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' }, color: theme.uploaded, textAlign: 'center' }}>
            {t('UploadSuccessful', 'Upload successful')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// UploadZone (single file)
// ---------------------------------------------------------------------------

interface UploadZoneProps {
  itemTitle: string;
  documentName: string;
  isUploaded: boolean;
  isExpired: boolean;
  theme: Theme;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
  onUploadSuccess: () => void;
}

const UploadZone: React.FC<UploadZoneProps> = ({
  itemTitle,
  documentName,
  isUploaded,
  isExpired,
  theme,
  authFetch,
  onUploadSuccess,
}) => {
  const { t } = useTranslation();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadState, setUploadState] = useState<'idle' | 'uploading' | 'success' | 'error'>(
    isUploaded && !isExpired ? 'success' : 'idle'
  );
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (file: File) => {
    const allowed = ['.pdf', '.doc', '.docx', '.png', '.jpg', '.jpeg'];
    const name = file.name.toLowerCase();
    const ext = name.substring(name.lastIndexOf('.'));
    if (!allowed.includes(ext)) {
      setUploadState('error');
      setErrorMessage(t('InvalidFileType', 'Invalid file type'));
      setTimeout(() => { setUploadState('idle'); setErrorMessage(''); }, 5000);
      return;
    }

    setUploadState('uploading');
    setUploadProgress(10);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentName', documentName);

      const response = await authFetch('/api/caregiver/files', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Upload failed');
      }

      setUploadProgress(100);
      setUploadState('success');
      onUploadSuccess();
    } catch (error: any) {
      console.error('Upload failed:', error);
      setUploadState('error');
      setErrorMessage(error.message || t('UploadFailed', 'Upload failed'));
      setTimeout(() => { setUploadState('idle'); setErrorMessage(''); }, 5000);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files?.length) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Card
      sx={{
        width: { xs: '100%', sm: 215 },
        margin: { xs: 0, sm: 1.5 },
        height: { xs: 'auto', sm: '100%' },
        display: 'flex',
        flexDirection: 'column',
        borderColor: theme.deep,
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': { boxShadow: '0 4px 8px rgba(51, 6, 98, 0.2)' },
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto',
          minHeight: { xs: 30, sm: 40 },
          height: { xs: 'auto', sm: 40 },
          padding: { xs: 0.5, sm: 1 },
          backgroundColor: uploadState === 'success' ? theme.uploaded : theme.notUploaded,
          color: 'white',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          textAlign: 'center',
          transition: 'background-color 0.3s ease',
        }}
      >
        <Typography
          variant="subtitle1"
          component="div"
          sx={{
            width: '100%',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            hyphens: 'auto',
            lineHeight: 1,
            fontSize: { xs: '0.65rem', sm: '0.875rem' },
          }}
        >
          {itemTitle}
        </Typography>
      </CardContent>

      <CardContent
        sx={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: { xs: 0.75, sm: 1.5 },
          cursor: 'pointer',
          border: `2px dashed ${uploadState === 'success' ? theme.uploaded : theme.notUploaded}`,
          borderTop: 'none',
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
          minHeight: { xs: 90, sm: 110 },
          height: { xs: 90, sm: 110 },
          transition: 'all 0.3s ease',
          backgroundColor: uploadState === 'success' ? `${theme.uploaded}10` : 'transparent',
          '&:hover': {
            backgroundColor: uploadState === 'success' ? `${theme.uploaded}10` : `${theme.medium}10`,
            borderColor: uploadState === 'success' ? theme.uploaded : theme.medium,
          },
        }}
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <input
          type="file"
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={(e) => e.target.files?.length && handleFileUpload(e.target.files[0])}
        />

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          {uploadState === 'idle' && (
            <>
              <CloudUploadIcon sx={{ fontSize: { xs: 20, sm: 30 }, color: theme.notUploaded, mb: 0.25 }} />
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: { xs: '0.6rem', sm: '0.875rem' }, textAlign: 'center', width: '100%', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word', hyphens: 'auto' }}
              >
                {t('DragFileOrBrowse', 'Drag file or browse')}
              </Typography>
            </>
          )}

          {uploadState === 'uploading' && (
            <>
              <CircularProgress variant="determinate" value={uploadProgress} size={uploadProgress > 0 ? 24 : 20} sx={{ mb: 0.25, color: theme.medium }} />
              <Typography variant="body2" sx={{ fontSize: { xs: '0.6rem', sm: '0.875rem' }, textAlign: 'center', width: '100%', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word', hyphens: 'auto' }}>
                {t('Uploading', { percentage: uploadProgress, defaultValue: 'Uploading... {{percentage}}%' })}
              </Typography>
            </>
          )}

          {uploadState === 'success' && (
            <>
              <CheckCircleIcon sx={{ fontSize: { xs: 20, sm: 30 }, color: theme.uploaded, mb: 0.25 }} />
              <Typography variant="body2" sx={{ fontSize: { xs: '0.6rem', sm: '0.875rem' }, color: theme.uploaded, textAlign: 'center', width: '100%', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word', hyphens: 'auto' }}>
                {t('UploadSuccessful', 'Upload successful')}
              </Typography>
            </>
          )}

          {uploadState === 'error' && (
            <>
              <ErrorIcon sx={{ fontSize: { xs: 20, sm: 30 }, color: 'error.main', mb: 0.25 }} />
              <Typography variant="body2" sx={{ fontSize: { xs: '0.6rem', sm: '0.875rem' }, color: 'error.main', textAlign: 'center', width: '100%', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word', hyphens: 'auto' }}>
                {errorMessage}
              </Typography>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// TabPanel
// ---------------------------------------------------------------------------

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index} id={`file-tabpanel-${index}`} aria-labelledby={`file-tab-${index}`}>
    {value === index && (
      <Box sx={{ p: 3 }}>
        <Typography component="div">{children}</Typography>
      </Box>
    )}
  </div>
);

function a11yProps(index: number) {
  return { id: `file-tab-${index}`, 'aria-controls': `file-tabpanel-${index}` };
}

// ---------------------------------------------------------------------------
// Main FileUpload component
// ---------------------------------------------------------------------------

const FileUpload: React.FC<FileUploadProps> = ({
  formData,
  formName,
  layout = 'tabs',
  themeName = 'default',
}) => {
  const { t } = useTranslation();
  const { accessToken, refreshAccessToken } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [caregiverFiles, setCaregiverFiles] = useState<NexusFile[]>([]);
  const [complianceItems, setComplianceItems] = useState<NexusComplianceItem[]>([]);
  const [complianceAvailable, setComplianceAvailable] = useState(false);
  const [expirations, setExpirations] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const hasFetchedInitialFiles = useRef(false);

  const theme = themes[themeName] || themes.default;
  const currentFormConfig = formConfig[formName];

  // Auth-aware fetch (matching SelfieUpload pattern)
  const authFetch = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const headers: Record<string, string> = { ...(options.headers as Record<string, string> || {}) };
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
      // Don't set Content-Type for FormData (browser sets multipart boundary)
      const response = await fetch(url, { ...options, headers });
      if (response.status === 401) {
        const refreshedToken = await refreshAccessToken();
        if (refreshedToken) {
          headers['Authorization'] = `Bearer ${refreshedToken}`;
          return fetch(url, { ...options, headers });
        }
      }
      return response;
    },
    [accessToken, refreshAccessToken]
  );

  // Fetch file list + expirations on mount
  const fetchFiles = useCallback(async () => {
    try {
      const response = await authFetch('/api/caregiver/files');
      if (response.ok) {
        const data = await response.json();
        setCaregiverFiles(data.files || []);
        setComplianceItems(Array.isArray(data.complianceItems) ? data.complianceItems : []);
        setComplianceAvailable(data.complianceAvailable === true);
        setExpirations(data.expirations || {});
      }
    } catch (err) {
      console.error('[FileUpload] Error fetching files:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    if (hasFetchedInitialFiles.current) {
      return;
    }

    hasFetchedInitialFiles.current = true;
    fetchFiles();
  }, [fetchFiles]);

  if (!currentFormConfig) {
    return (
      <Alert severity="error">
        Configuration error: Form &quot;{formName}&quot; not found.
      </Alert>
    );
  }

  const handleUploadSuccess = () => {
    // Refresh file list after upload
    fetchFiles();
    setNotification({ open: true, message: t('UploadSuccessful', 'Upload successful'), severity: 'success' });
  };

  const isExpired = (expirationField?: string): boolean => {
    if (!expirationField) return false;
    // Check expirations from Nexus first, fall back to formData
    const val = expirations[expirationField] || formData[expirationField];
    if (!val) return false;
    const expDate = new Date(val);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return expDate < today;
  };

  const tabs = currentFormConfig.tabs
    .map((tab) => {
      const complianceSectionApiName = COMPLIANCE_SECTION_BY_TAB[tab.label];
      return {
        ...tab,
        translatedLabel: t(tab.label, tab.label),
        translatedDocuments: tab.documents
          .filter((doc) => isDocumentRelevantToCompliance(
            doc.name,
            complianceItems,
            complianceAvailable,
            complianceSectionApiName
          ))
          .map((doc) => ({
            ...doc,
            translated: t(doc.name.replace(/\s+/g, '_'), doc.name),
            uploaded: isDocumentUploaded(doc.name, caregiverFiles)
              || isDocumentSatisfiedByCompliance(doc.name, complianceItems),
            expired: isExpired(doc.expirationField),
          })),
      };
    })
    .filter((tab) => tab.translatedDocuments.length > 0);

  const renderDocumentGrid = (docs: typeof tabs[0]['translatedDocuments']) => (
    <Grid2
      container
      spacing={{ xs: 1, sm: 2 }}
      justifyContent={{
        xs: 'center',
        sm: docs.length % 2 !== 0 ? 'center' : 'flex-start',
        md: docs.length % 3 !== 0 ? 'center' : 'flex-start',
        lg: docs.length % 4 !== 0 ? 'center' : 'flex-start',
      }}
    >
      {docs.map((doc) => (
        <Grid2
          key={doc.name}
          size={{ xs: 6, sm: 6, md: 4, lg: 3 }}
          sx={{ display: 'flex', justifyContent: 'center' }}
        >
          {doc.twoSided ? (
            <TwoSidedUploadZone
              itemTitle={doc.translated}
              documentName={doc.name}
              isUploaded={doc.uploaded}
              isExpired={doc.expired}
              theme={theme}
              authFetch={authFetch}
              onUploadSuccess={handleUploadSuccess}
            />
          ) : (
            <UploadZone
              itemTitle={doc.translated}
              documentName={doc.name}
              isUploaded={doc.uploaded}
              isExpired={doc.expired}
              theme={theme}
              authFetch={authFetch}
              onUploadSuccess={handleUploadSuccess}
            />
          )}
        </Grid2>
      ))}
    </Grid2>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
      }}
    >
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={notification.severity}
          action={
            <IconButton size="small" color="inherit" onClick={() => setNotification({ ...notification, open: false })}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {layout === 'tabs' ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              aria-label="document upload tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              TabIndicatorProps={{
                style: { backgroundColor: themeName === 'validation' ? '#000000' : theme.deep },
              }}
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 'bold',
                  color: 'rgba(0, 0, 0, 0.6)',
                  textTransform: 'none',
                  whiteSpace: 'normal',
                  lineHeight: 1.2,
                  '&.Mui-selected': { color: themeName === 'validation' ? '#000000' : theme.deep },
                  '&:hover': { color: themeName === 'validation' ? 'rgba(0, 0, 0, 0.8)' : theme.medium, opacity: 1 },
                },
              }}
            >
              {tabs.map((tab, index) => (
                <Tab key={index} label={tab.translatedLabel} {...a11yProps(index)} />
              ))}
            </Tabs>
          </Box>

          {tabs.map((tab, index) => (
            <TabPanel key={index} value={tabValue} index={index}>
              {renderDocumentGrid(tab.translatedDocuments)}
            </TabPanel>
          ))}
        </>
      ) : (
        <Box sx={{ p: 3 }}>
          {tabs.map((tab, tabIndex) => (
            <Box key={tabIndex} sx={{ mb: tabIndex < tabs.length - 1 ? 4 : 0 }}>
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  mb: 2,
                  pb: 1,
                  borderBottom: `2px solid ${themeName === 'validation' ? '#000000' : theme.deep}`,
                  color: themeName === 'validation' ? '#000000' : theme.deep,
                  fontWeight: 'bold',
                }}
              >
                {tab.translatedLabel}
              </Typography>
              {renderDocumentGrid(tab.translatedDocuments)}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default FileUpload;
